/**
 * 初期化のStateを扱うモジュール
 */
import { ThunkDispatch } from 'redux-thunk';

import { ActionsUnion, createAction } from 'common/utils/action-utils';
import {
  deleteCookie,
  loadCookie,
  SasCookieKeys,
  saveCookie,
  VALUE_DUMMY_REGION
} from 'common/utils/cookie-utils';
import { setStatus, BoardStatus } from './board';
import { getCountryMaster, getLanguageMaster } from './master';
import {
  getUser,
  updateUser,
  UpdateKeyValue,
  LOGIN_USER
} from '../domain/user';
import { loggedIn, loggedOut, reLogin } from './principal';
import { getGlossaryAll } from './glossaries';
import { AppState } from 'pages';
import { FetchStatus } from './fetch';

// Traditional Chineseの使用地域
const TC_AREA = ['HK', 'TW', 'MO'];

enum ActionTypes {
  START = 'app/initialize/START',
  FINISH = 'app/initialize/FINISH'
}

const startInitialization = () => createAction(ActionTypes.START);
const finishInitialization = () => createAction(ActionTypes.FINISH);

// ブラウザの言語から言語を取得する。
// 中国語以外は言語のみ使用し、中国語のみ地域によって切り替える
export const getBrowserLanguage = () => {
  const browserLang = window.navigator.language;
  const regex = /^([a-z]{2})-?([A-Z]{2})?$/;
  const [_, language, region] = browserLang.match(regex) || ['', 'en', ''];
  if (language === 'zh') {
    return TC_AREA.includes(region) ? 'tc' : language;
  }
  return language;
};

/**
 * 初期化処理を行う
 */
export const initialize = (): ((
  dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
  getState: () => AppState
) => Promise<void>) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    dispatch(startInitialization());
    // クエリー指定の言語情報を取得
    const state = getState();
    const _searchParams = new URLSearchParams(state.router.location.search);
    const _language = _searchParams.get('language') || '';
    const cookie = loadCookie();
    const regionCookie = cookie[SasCookieKeys.KEY_REGION];

    const getMasters = async () =>
      await Promise.all([
        dispatch(getLanguageMaster()),
        dispatch(getCountryMaster())
      ]);

    const getExistCountry = (user_country: string) => {
      const state = getState();
      const _master = state.app.master;
      for (const [key, value] of Object.entries(_master.country)) {
        // 国リストに存在するか確認
        for (const country of value.countries) {
          if (country.code === user_country) {
            return user_country;
          }
        }
      }
      return '';
    };

    const getInitInfo = async () => {
      let loggedInFlag = false;
      // Cookie取得
      const cookie = loadCookie();
      if (cookie[SasCookieKeys.KEY_TOKEN]) {
        // ログイン済みの場合
        await dispatch(getUser(cookie[SasCookieKeys.KEY_TOKEN]));
        const state = getState();
        const country = state.domain.user.country;
        const language = state.domain.user.language;
        if (country && language) {
          // ログイン済みに設定
          loggedInFlag = true;
          dispatch(loggedIn());
        }
        // アクセストークン期限切れで再ログイン予定か確認
        const loginFetch = state.app.fetch[LOGIN_USER];
        if (loginFetch === FetchStatus.UNTREATED) {
          dispatch(reLogin());
        }
      }
      if (!loggedInFlag) {
        // 再ログインでの購入確認画面表示のクッキーがあった場合は削除
        // 戻るボタンで戻った場合を想定
        deleteCookie(SasCookieKeys.KEY_BEFORE_PURCHASE);
        dispatch(loggedOut());
      }
    };

    const getCountryAndLanguage = async () => {
      const state = getState();
      const cookie = loadCookie();

      // 優先度順に利用する国・言語を取得
      let country =
        state.domain.user.country || cookie[SasCookieKeys.KEY_REGION] || '';
      const language =
        _language ||
        state.domain.user.language ||
        cookie[SasCookieKeys.KEY_LANGUAGE] ||
        getBrowserLanguage();

      // 利用する国情報でユーザー更新とクッキーの記録を行う
      // null or undefinedの場合は空文字に変換
      country = country ? country : '';
      await dispatch(updateUser(UpdateKeyValue.KEY_COUNTRY, country));
      saveCookie(SasCookieKeys.KEY_REGION, country, 180);
      if (country) {
        // SASに対応する国情報が存在する場合は、紐づく地域も取得する
        getRegion();
      }

      // 利用する言語情報でユーザー更新とクッキー記録を行う
      // 言語情報はSAS非対応だったとしても、グロッサリー取得時に英語に自動変換されるのでそのまま登録
      await dispatch(updateUser(UpdateKeyValue.KEY_LANGUAGE, language));
      saveCookie(SasCookieKeys.KEY_LANGUAGE, language, 180);
    };

    const getRegion = () => {
      let region = '';
      const state = getState();
      const _master = state.app.master;
      const user_country = state.domain.user.country;
      for (const [key, value] of Object.entries(_master.country)) {
        // 地域に所属する国リストに存在するか確認
        for (const country of value.countries) {
          if (country.code === user_country) {
            region = key;
            break;
          }
        }
        // 所属する地域が確定したらループを抜ける
        if (region) {
          dispatch(updateUser(UpdateKeyValue.KEY_REGION, region));
          break;
        }
      }
    };

    // マスターファイルと、ユーザー・グロッサリ取得は並列取得
    await Promise.all([getMasters(), getInitInfo()]);

    // 言語・国・地域を設定
    await getCountryAndLanguage();

    await dispatch(getGlossaryAll());

    const country = getState().domain.user.country;
    if (!country && regionCookie !== VALUE_DUMMY_REGION) {
      // 国未選択状態で言語選択した場合は表示しない(初回アクセス画面経由は表示)
      dispatch(setStatus(BoardStatus.FIRST_DISP));
    }

    dispatch(finishInitialization());
  };
};

const actions = {
  startInitialization,
  finishInitialization
};
type InitializeAction = ActionsUnion<typeof actions>;

/**
 * 初期化処理の状態
 */
export enum InitializeStatus {
  NOT_START,
  START,
  FINISH
}
const initialState: InitializeState = {
  status: InitializeStatus.NOT_START
};

/**
 * 初期化処理のState
 */
export interface InitializeState {
  /** 初期化状態 */
  status: InitializeStatus;
}

export default (
  state: InitializeState = initialState,
  actions: InitializeAction
): InitializeState => {
  switch (actions.type) {
    case ActionTypes.START:
      return {
        ...state,
        status: InitializeStatus.START
      };
    case ActionTypes.FINISH:
      return {
        ...state,
        status: InitializeStatus.FINISH
      };
    default:
      return state;
  }
};
